import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import API_BASE_URL from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';

const ROUTE_SCOPE = [
  [/^\/(home)?$/, 'home'],
  [/^\/membership/, 'membership'],
  [/^\/contact/, 'contact'],
  [/^\/donate/, 'donate'],
  [/^\/gallery/, 'gallery'],
  [/^\/shop/, 'shop'],
  [/^\/blog/, 'blog'],
  [/^\/faq/, 'faq'],
  [/^\/carte/, 'carte'],
  [/^\/(equipe|founders)/, 'equipe'],
];

const scopeForPath = (path) => {
  for (const [re, s] of ROUTE_SCOPE) if (re.test(path)) return s;
  return null;
};

const POS_CSS = { top: 'center top', bottom: 'center bottom', center: 'center' };

const pickRandom = (len, exclude) => {
  if (len <= 1) return 0;
  let r = Math.floor(Math.random() * len);
  if (r === exclude) r = (r + 1) % len;
  return r;
};

// Fond photo plein écran par page. Source d'images :
//  - images propres à la page si définies dans BackgroundSettings,
//  - sinon la BIBLIOTHÈQUE DE FONDS dédiée (pool) tirée ALÉATOIREMENT.
// Rotation automatique en fondu (15 s par défaut pour le pool), cadrage + voile par thème.
export const PageBackground = ({ scope: scopeProp }) => {
  const { theme } = useTheme();
  const location = useLocation();
  const scope = scopeProp || scopeForPath(location.pathname);
  const [cfg, setCfg] = useState(null);
  const [lib, setLib] = useState(null);
  const [curIdx, setCurIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/site/bg-library`);
        if (!cancelled) setLib(res.data);
      } catch (e) { if (!cancelled) setLib({ light: [], dark: [], interval: 15 }); }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!scope) { setCfg(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/site/backgrounds`, { params: { scope } });
        if (!cancelled) setCfg(res.data);
      } catch (e) { if (!cancelled) setCfg(null); }
    })();
    return () => { cancelled = true; };
  }, [scope]);

  // Images propres à la page (si l'admin en a défini pour ce scope + thème)
  const pageImgs = useMemo(() => {
    if (!cfg) return [];
    const arr = theme === 'light' ? cfg.light_images : cfg.dark_images;
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  }, [cfg, theme]);

  const poolImgs = useMemo(() => {
    if (!lib) return [];
    const arr = theme === 'light' ? lib.light : lib.dark;
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  }, [lib, theme]);

  // Priorité : images de la page, sinon pool de la bibliothèque de fonds.
  const usingPool = pageImgs.length === 0;
  const imgs = usingPool ? poolImgs : pageImgs;

  const interval = usingPool
    ? Number(lib?.interval != null ? lib.interval : 15)
    : Number(cfg?.rotate_interval || 0);

  useEffect(() => {
    if (!imgs.length) return undefined;
    setCurIdx(pickRandom(imgs.length, -1));
    if (interval > 0 && imgs.length > 1) {
      const id = setInterval(() => setCurIdx((prev) => pickRandom(imgs.length, prev)), interval * 1000);
      return () => clearInterval(id);
    }
    return undefined;
  }, [imgs, interval]);

  if (!scope || !cfg || cfg.enabled === false || !imgs.length) return null;

  const pct = theme === 'light'
    ? (cfg.light_intensity != null ? cfg.light_intensity : 60)
    : (cfg.dark_intensity != null ? cfg.dark_intensity : 50);
  const alpha = Math.max(0, Math.min(100, pct)) / 100;
  const position = POS_CSS[theme === 'light' ? cfg.light_position : cfg.dark_position] || 'center';

  return (
    <>
      <div className="fixed inset-0 z-0 overflow-hidden" aria-hidden="true" data-testid="page-background">
        {imgs.map((img, i) => (
          <div
            key={`${img}-${i}`}
            className="absolute inset-0 bg-cover animate-slow-zoom motion-reduce:animate-none"
            style={{
              backgroundImage: `url(${img})`,
              backgroundPosition: position,
              opacity: i === curIdx ? 1 : 0,
              transition: 'opacity 1.5s ease-in-out',
            }}
          />
        ))}
      </div>
      <div
        className="fixed inset-0 z-0"
        aria-hidden="true"
        data-testid="page-background-overlay"
        style={{ backgroundColor: `hsl(var(--background) / ${alpha})` }}
      />
    </>
  );
};

export default PageBackground;
